/**
 * CorporateDocumentsStep Component
 * =================================
 * Upload corporate documents for KYB verification.
 * Supports multiple document types with drag-and-drop upload,
 * progress indicators, and required/optional document handling.
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  FileText,
  ChevronLeft,
  ChevronRight,
  AlertCircle,
  Upload,
  Check,
  X,
  File,
  Image,
  Loader2,
  FileCheck,
  Building,
  Scale,
  Award,
  Landmark,
} from 'lucide-react';

const DEFAULT_DOCUMENT_TYPES = [
  {
    id: 'certificate_of_incorporation',
    label: 'Certificate of Incorporation',
    description: 'Official document proving company registration',
    icon: Award,
    required: true,
    acceptedFormats: ['pdf', 'jpg', 'jpeg', 'png'],
  },
  {
    id: 'articles_of_association',
    label: 'Articles of Association',
    description: 'Company bylaws and operating rules',
    icon: FileText,
    required: true,
    acceptedFormats: ['pdf', 'jpg', 'jpeg', 'png'],
  },
  {
    id: 'proof_of_address',
    label: 'Proof of Business Address',
    description: 'Utility bill or bank statement (within 3 months)',
    icon: Building,
    required: true,
    acceptedFormats: ['pdf', 'jpg', 'jpeg', 'png'],
  },
  {
    id: 'ownership_structure',
    label: 'Ownership Structure Document',
    description: 'Shareholder register or org chart',
    icon: Scale,
    required: false,
    acceptedFormats: ['pdf', 'jpg', 'jpeg', 'png'],
  },
  {
    id: 'good_standing',
    label: 'Certificate of Good Standing',
    description: 'Recent certificate from registrar',
    icon: FileCheck,
    required: false,
    acceptedFormats: ['pdf', 'jpg', 'jpeg', 'png'],
  },
  {
    id: 'tax_registration',
    label: 'Tax Registration Certificate',
    description: 'EIN confirmation or equivalent',
    icon: Landmark,
    required: false,
    acceptedFormats: ['pdf', 'jpg', 'jpeg', 'png'],
  },
];

const formatFileSize = (bytes) => {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

const getFileExtension = (filename) => {
  return filename.split('.').pop().toLowerCase();
};

const DocumentUploadCard = ({
  docType,
  uploadedFile,
  uploadProgress,
  isUploading,
  error,
  onUpload,
  onRemove,
}) => {
  const inputRef = useRef(null);
  const [isDragging, setIsDragging] = useState(false);

  const IconComponent = docType.icon || FileText;

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files[0];
    if (file) {
      onUpload(docType.id, file);
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      onUpload(docType.id, file);
    }
    // Reset input so same file can be selected again
    e.target.value = '';
  };

  const isComplete = uploadedFile && !isUploading && !error;

  return (
    <div className={`document-card ${isComplete ? 'complete' : ''} ${error ? 'has-error' : ''}`}>
      <div className="document-card-header">
        <div className={`document-icon ${isComplete ? 'success' : ''}`}>
          <IconComponent size={20} />
        </div>
        <div className="document-info">
          <div className="document-label">
            {docType.label}
            {docType.required && <span className="required-badge">Required</span>}
          </div>
          <div className="document-description">{docType.description}</div>
        </div>
        {isComplete && (
          <div className="complete-badge">
            <Check size={16} />
          </div>
        )}
      </div>

      {uploadedFile && !error ? (
        <div className="uploaded-file">
          <div className="file-preview">
            {uploadedFile.type?.startsWith('image/') ? (
              <Image size={18} />
            ) : (
              <File size={18} />
            )}
            <div className="file-details">
              <span className="file-name">{uploadedFile.name}</span>
              <span className="file-size">{formatFileSize(uploadedFile.size)}</span>
            </div>
          </div>
          {isUploading ? (
            <div className="upload-progress">
              <div
                className="progress-bar"
                style={{ width: `${uploadProgress}%` }}
              />
              <span className="progress-text">{uploadProgress}%</span>
            </div>
          ) : (
            <button
              type="button"
              className="remove-file-btn"
              onClick={() => onRemove(docType.id)}
            >
              <X size={16} />
            </button>
          )}
        </div>
      ) : (
        <div
          className={`upload-zone ${isDragging ? 'dragging' : ''}`}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          onClick={() => inputRef.current?.click()}
        >
          <input
            ref={inputRef}
            type="file"
            accept={docType.acceptedFormats.map(f => `.${f}`).join(',')}
            onChange={handleFileSelect}
            style={{ display: 'none' }}
          />
          {isUploading ? (
            <Loader2 size={24} className="spin" />
          ) : (
            <>
              <Upload size={24} />
              <span className="upload-text">
                Drop file here or <span className="upload-link">browse</span>
              </span>
              <span className="upload-formats">
                {docType.acceptedFormats.map(f => f.toUpperCase()).join(', ')}
              </span>
            </>
          )}
        </div>
      )}

      {error && (
        <div className="document-error">
          <AlertCircle size={14} />
          {error}
        </div>
      )}
    </div>
  );
};

export default function CorporateDocumentsStep({
  config = {},
  branding = {},
  sessionData = {},
  onComplete,
  onBack,
  onError,
  apiClient,
}) {
  const [animateIn, setAnimateIn] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState({});

  // Document state
  const [uploadedFiles, setUploadedFiles] = useState(() => {
    // Initialize from session data if resuming
    const initial = {};
    if (sessionData?.corporate_documents) {
      Object.keys(sessionData.corporate_documents).forEach(docId => {
        const doc = sessionData.corporate_documents[docId];
        if (doc) {
          initial[docId] = {
            name: doc.file_name || 'Uploaded document',
            size: doc.file_size || 0,
            type: doc.content_type,
            uploaded: true,
            document_id: doc.document_id,
          };
        }
      });
    }
    return initial;
  });
  const [uploadProgress, setUploadProgress] = useState({});
  const [uploading, setUploading] = useState({});

  // Get document types from config or use defaults
  const documentTypes = config?.document_types || DEFAULT_DOCUMENT_TYPES;

  useEffect(() => {
    const timer = setTimeout(() => setAnimateIn(true), 50);
    return () => clearTimeout(timer);
  }, []);

  const handleUpload = useCallback(async (docTypeId, file) => {
    // Validate file
    const docType = documentTypes.find(d => d.id === docTypeId);
    const extension = getFileExtension(file.name);

    if (!docType.acceptedFormats.includes(extension)) {
      setErrors(prev => ({
        ...prev,
        [docTypeId]: `Invalid file format. Accepted: ${docType.acceptedFormats.join(', ')}`,
      }));
      return;
    }

    // Check file size (max 10MB)
    const maxSize = 10 * 1024 * 1024;
    if (file.size > maxSize) {
      setErrors(prev => ({
        ...prev,
        [docTypeId]: 'File size must be less than 10MB',
      }));
      return;
    }

    // Clear any existing error
    setErrors(prev => ({ ...prev, [docTypeId]: null }));
    setUploading(prev => ({ ...prev, [docTypeId]: true }));
    setUploadProgress(prev => ({ ...prev, [docTypeId]: 0 }));

    try {
      // Set file in state immediately for preview
      setUploadedFiles(prev => ({
        ...prev,
        [docTypeId]: {
          name: file.name,
          size: file.size,
          type: file.type,
          file: file,
        },
      }));

      // Simulate progress while uploading
      const progressInterval = setInterval(() => {
        setUploadProgress(prev => {
          const current = prev[docTypeId] || 0;
          if (current >= 90) {
            clearInterval(progressInterval);
            return prev;
          }
          return { ...prev, [docTypeId]: current + 10 };
        });
      }, 200);

      // Upload to API if available
      if (apiClient?.uploadDocument) {
        const result = await apiClient.uploadDocument(file, docTypeId, 'front');

        clearInterval(progressInterval);
        setUploadProgress(prev => ({ ...prev, [docTypeId]: 100 }));

        // Update file state with document ID
        setUploadedFiles(prev => ({
          ...prev,
          [docTypeId]: {
            ...prev[docTypeId],
            uploaded: true,
            document_id: result.document_id,
          },
        }));
      } else {
        // Mock upload for demo
        await new Promise(resolve => setTimeout(resolve, 1500));
        clearInterval(progressInterval);
        setUploadProgress(prev => ({ ...prev, [docTypeId]: 100 }));

        setUploadedFiles(prev => ({
          ...prev,
          [docTypeId]: {
            ...prev[docTypeId],
            uploaded: true,
            document_id: `mock_${docTypeId}_${Date.now()}`,
          },
        }));
      }

      // Clear progress after a moment
      setTimeout(() => {
        setUploadProgress(prev => ({ ...prev, [docTypeId]: 0 }));
      }, 500);
    } catch (err) {
      console.error(`Failed to upload ${docTypeId}:`, err);
      setErrors(prev => ({
        ...prev,
        [docTypeId]: err.message || 'Upload failed. Please try again.',
      }));
      setUploadedFiles(prev => {
        const newFiles = { ...prev };
        delete newFiles[docTypeId];
        return newFiles;
      });
    } finally {
      setUploading(prev => ({ ...prev, [docTypeId]: false }));
    }
  }, [apiClient, documentTypes]);

  const handleRemove = useCallback((docTypeId) => {
    setUploadedFiles(prev => {
      const newFiles = { ...prev };
      delete newFiles[docTypeId];
      return newFiles;
    });
    setErrors(prev => ({ ...prev, [docTypeId]: null }));
  }, []);

  const validateForm = useCallback(() => {
    const newErrors = {};

    // Check all required documents are uploaded
    documentTypes.forEach(docType => {
      if (docType.required && !uploadedFiles[docType.id]?.uploaded) {
        newErrors[docType.id] = `${docType.label} is required`;
      }
    });

    setErrors(prev => ({ ...prev, ...newErrors }));
    return Object.keys(newErrors).length === 0;
  }, [documentTypes, uploadedFiles]);

  const handleSubmit = async () => {
    if (!validateForm()) return;

    setSubmitting(true);
    try {
      // Format document data for API
      const documentsData = {};
      Object.keys(uploadedFiles).forEach(docId => {
        const file = uploadedFiles[docId];
        if (file.uploaded) {
          documentsData[docId] = {
            document_id: file.document_id,
            file_name: file.name,
            file_size: file.size,
            content_type: file.type,
          };
        }
      });

      // Submit to API if client available
      if (apiClient?.completeStep) {
        await apiClient.completeStep('corporate_documents', {
          documents: documentsData,
        });
      }

      onComplete({ corporate_documents: documentsData });
    } catch (err) {
      console.error('Failed to submit documents:', err);
      if (onError) {
        onError(err);
      }
      setErrors(prev => ({ ...prev, submit: err.message || 'Failed to save documents' }));
    } finally {
      setSubmitting(false);
    }
  };

  // Calculate progress
  const requiredDocs = documentTypes.filter(d => d.required);
  const uploadedRequiredDocs = requiredDocs.filter(d => uploadedFiles[d.id]?.uploaded);
  const progress = requiredDocs.length > 0
    ? Math.round((uploadedRequiredDocs.length / requiredDocs.length) * 100)
    : 100;

  return (
    <div className={`sdk-step corporate-documents-step ${animateIn ? 'animate-in' : ''}`}>
      <div className="step-header">
        {onBack && (
          <button className="back-button" onClick={onBack} type="button">
            <ChevronLeft size={20} />
          </button>
        )}
        <div className="step-icon">
          <FileText size={24} />
        </div>
      </div>

      <h2 className="step-title">Corporate Documents</h2>
      <p className="step-description">
        Upload the required documents to verify your business.
      </p>

      {/* Progress indicator */}
      <div className="documents-progress">
        <div className="progress-header">
          <span className="progress-label">Upload Progress</span>
          <span className="progress-value">{progress}% complete</span>
        </div>
        <div className="progress-track">
          <div
            className="progress-fill"
            style={{ width: `${progress}%` }}
          />
        </div>
        <span className="progress-status">
          {uploadedRequiredDocs.length} of {requiredDocs.length} required documents uploaded
        </span>
      </div>

      {/* Required Documents */}
      <div className="documents-section">
        <h3 className="section-label">Required Documents</h3>
        <div className="documents-grid">
          {documentTypes.filter(d => d.required).map(docType => (
            <DocumentUploadCard
              key={docType.id}
              docType={docType}
              uploadedFile={uploadedFiles[docType.id]}
              uploadProgress={uploadProgress[docType.id] || 0}
              isUploading={uploading[docType.id]}
              error={errors[docType.id]}
              onUpload={handleUpload}
              onRemove={handleRemove}
            />
          ))}
        </div>
      </div>

      {/* Optional Documents */}
      {documentTypes.some(d => !d.required) && (
        <div className="documents-section">
          <h3 className="section-label">Optional Documents</h3>
          <div className="documents-grid">
            {documentTypes.filter(d => !d.required).map(docType => (
              <DocumentUploadCard
                key={docType.id}
                docType={docType}
                uploadedFile={uploadedFiles[docType.id]}
                uploadProgress={uploadProgress[docType.id] || 0}
                isUploading={uploading[docType.id]}
                error={errors[docType.id]}
                onUpload={handleUpload}
                onRemove={handleRemove}
              />
            ))}
          </div>
        </div>
      )}

      {errors.submit && (
        <div className="submit-error">
          <AlertCircle size={16} />
          {errors.submit}
        </div>
      )}

      <div className="step-actions">
        <button
          type="button"
          className="primary-button"
          onClick={handleSubmit}
          disabled={submitting || progress < 100}
        >
          {submitting ? (
            <>
              <Loader2 size={18} className="spin" />
              Processing...
            </>
          ) : (
            <>
              Continue
              <ChevronRight size={18} />
            </>
          )}
        </button>
      </div>

      <style>{corporateDocumentsStyles}</style>
    </div>
  );
}

const corporateDocumentsStyles = `
  .corporate-documents-step {
    padding: 24px;
    max-width: 640px;
    margin: 0 auto;
  }

  .step-header {
    display: flex;
    align-items: center;
    gap: 16px;
    margin-bottom: 16px;
  }

  .back-button {
    background: none;
    border: none;
    cursor: pointer;
    padding: 8px;
    color: var(--sdk-text-secondary, #6B7280);
    border-radius: 8px;
    transition: all 0.2s;
  }

  .back-button:hover {
    background: var(--sdk-bg-secondary, #F3F4F6);
    color: var(--sdk-text, #1F2937);
  }

  .step-icon {
    width: 48px;
    height: 48px;
    border-radius: 12px;
    background: var(--sdk-primary, #0D9488);
    color: white;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .step-title {
    font-size: 24px;
    font-weight: 600;
    color: var(--sdk-text, #1F2937);
    margin: 0 0 8px;
  }

  .step-description {
    color: var(--sdk-text-secondary, #6B7280);
    font-size: 15px;
    margin: 0 0 24px;
    line-height: 1.5;
  }

  /* Progress */
  .documents-progress {
    background: var(--sdk-bg-secondary, #F9FAFB);
    border-radius: 12px;
    padding: 16px;
    margin-bottom: 24px;
  }

  .progress-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 10px;
  }

  .progress-label {
    font-size: 13px;
    font-weight: 500;
    color: var(--sdk-text-secondary, #6B7280);
  }

  .progress-value {
    font-size: 14px;
    font-weight: 600;
    color: var(--sdk-primary, #0D9488);
  }

  .progress-track {
    height: 8px;
    background: var(--sdk-border, #E5E7EB);
    border-radius: 4px;
    overflow: hidden;
    margin-bottom: 8px;
  }

  .progress-fill {
    height: 100%;
    background: var(--sdk-primary, #0D9488);
    border-radius: 4px;
    transition: width 0.3s ease;
  }

  .progress-status {
    font-size: 12px;
    color: var(--sdk-text-secondary, #9CA3AF);
  }

  /* Documents Section */
  .documents-section {
    margin-bottom: 24px;
  }

  .section-label {
    font-size: 13px;
    font-weight: 600;
    color: var(--sdk-text-secondary, #6B7280);
    text-transform: uppercase;
    letter-spacing: 0.05em;
    margin: 0 0 12px;
  }

  .documents-grid {
    display: flex;
    flex-direction: column;
    gap: 12px;
  }

  /* Document Card */
  .document-card {
    background: white;
    border: 2px solid var(--sdk-border, #E5E7EB);
    border-radius: 14px;
    overflow: hidden;
    transition: border-color 0.2s, box-shadow 0.2s;
  }

  .document-card.complete {
    border-color: var(--sdk-primary, #0D9488);
  }

  .document-card.has-error {
    border-color: #FECACA;
  }

  .document-card-header {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 14px 16px;
    background: var(--sdk-bg-secondary, #F9FAFB);
  }

  .document-icon {
    width: 40px;
    height: 40px;
    border-radius: 10px;
    background: white;
    border: 1px solid var(--sdk-border, #E5E7EB);
    color: var(--sdk-text-secondary, #6B7280);
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    transition: all 0.2s;
  }

  .document-icon.success {
    background: var(--sdk-primary, #0D9488);
    border-color: var(--sdk-primary, #0D9488);
    color: white;
  }

  .document-info {
    flex: 1;
    min-width: 0;
  }

  .document-label {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 14px;
    font-weight: 600;
    color: var(--sdk-text, #1F2937);
  }

  .required-badge {
    font-size: 10px;
    font-weight: 600;
    text-transform: uppercase;
    color: #DC2626;
    background: #FEE2E2;
    padding: 2px 6px;
    border-radius: 4px;
  }

  .document-description {
    font-size: 12px;
    color: var(--sdk-text-secondary, #9CA3AF);
    margin-top: 2px;
  }

  .complete-badge {
    width: 28px;
    height: 28px;
    border-radius: 50%;
    background: #D1FAE5;
    color: #059669;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
  }

  /* Upload Zone */
  .upload-zone {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 8px;
    padding: 24px 16px;
    border: 2px dashed var(--sdk-border, #E5E7EB);
    border-radius: 0 0 12px 12px;
    margin: 8px;
    cursor: pointer;
    transition: all 0.2s;
    color: var(--sdk-text-secondary, #6B7280);
  }

  .upload-zone:hover,
  .upload-zone.dragging {
    border-color: var(--sdk-primary, #0D9488);
    background: rgba(var(--sdk-primary-rgb, 13, 148, 136), 0.05);
  }

  .upload-text {
    font-size: 14px;
  }

  .upload-link {
    color: var(--sdk-primary, #0D9488);
    font-weight: 600;
    text-decoration: underline;
  }

  .upload-formats {
    font-size: 11px;
    color: var(--sdk-text-secondary, #9CA3AF);
  }

  /* Uploaded File */
  .uploaded-file {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 12px 16px;
  }

  .file-preview {
    display: flex;
    align-items: center;
    gap: 10px;
    color: var(--sdk-text-secondary, #6B7280);
  }

  .file-details {
    display: flex;
    flex-direction: column;
  }

  .file-name {
    font-size: 13px;
    font-weight: 500;
    color: var(--sdk-text, #1F2937);
    max-width: 200px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .file-size {
    font-size: 11px;
    color: var(--sdk-text-secondary, #9CA3AF);
  }

  .upload-progress {
    display: flex;
    align-items: center;
    gap: 10px;
    flex: 1;
    max-width: 120px;
    margin-left: auto;
  }

  .upload-progress .progress-bar {
    flex: 1;
    height: 4px;
    background: var(--sdk-border, #E5E7EB);
    border-radius: 2px;
    overflow: hidden;
  }

  .upload-progress .progress-bar::after {
    content: '';
    display: block;
    height: 100%;
    background: var(--sdk-primary, #0D9488);
    border-radius: 2px;
    transition: width 0.2s;
  }

  .progress-text {
    font-size: 12px;
    font-weight: 600;
    color: var(--sdk-primary, #0D9488);
    min-width: 36px;
    text-align: right;
  }

  .remove-file-btn {
    padding: 6px;
    background: none;
    border: none;
    color: var(--sdk-text-secondary, #9CA3AF);
    cursor: pointer;
    border-radius: 6px;
    transition: all 0.2s;
  }

  .remove-file-btn:hover {
    background: #FEE2E2;
    color: #EF4444;
  }

  /* Document Error */
  .document-error {
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 10px 16px;
    background: #FEF2F2;
    color: #DC2626;
    font-size: 13px;
  }

  /* Submit Error */
  .submit-error {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 12px 16px;
    background: #FEF2F2;
    border: 1px solid #FECACA;
    border-radius: 10px;
    color: #DC2626;
    font-size: 14px;
    margin-top: 16px;
  }

  /* Actions */
  .step-actions {
    margin-top: 24px;
  }

  .primary-button {
    width: 100%;
    padding: 14px 24px;
    background: var(--sdk-primary, #0D9488);
    color: white;
    border: none;
    border-radius: 12px;
    font-size: 16px;
    font-weight: 600;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    transition: all 0.2s;
  }

  .primary-button:hover:not(:disabled) {
    background: var(--sdk-primary-dark, #0F766E);
    transform: translateY(-1px);
  }

  .primary-button:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }

  .spin {
    animation: spin 1s linear infinite;
  }

  @keyframes spin {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  }

  @media (max-width: 480px) {
    .document-card-header {
      flex-wrap: wrap;
    }

    .file-name {
      max-width: 140px;
    }
  }
`;
